import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";

const initial = { name: "", email: "", phone: "", company: "", product: "", message: "" };

export default function Contact() {
    const { t } = useLang();
    const [form, setForm] = useState(initial);
    const [products, setProducts] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error(t.contact.tFill);
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/inquiries", form);
            toast.success(t.contact.tSuccess);
            setForm(initial);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || t.contact.tError);
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls = "w-full bg-background border border-border rounded-md px-4 py-3 text-obsidian placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-cyan focus:border-transparent transition-shadow";

    return (
        <div className="bg-background min-h-screen">
            <Header />
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-36 pb-32">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
                    <div className="lg:col-span-5">
                        <Reveal className="eyebrow text-cyan mb-6">{t.contact.eyebrow}</Reveal>
                        <Reveal delay={0.05}>
                            <h1 className="font-heading font-black text-5xl sm:text-6xl tracking-tighter text-obsidian leading-[0.88]">
                                {t.contact.title}
                            </h1>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-md">{t.contact.sub}</p>
                        </Reveal>
                        <Reveal delay={0.15} className="mt-12 space-y-5">
                            {[
                                { icon: Mail, k: t.contact.emailL, v: "contact@korhcgroup.com" },
                                { icon: Phone, k: t.contact.phoneL, v: "+82 10 4769 3892" },
                                { icon: MapPin, k: t.contact.hqL, v: "Seoul, South Korea" },
                            ].map((c, i) => (
                                <div key={i} className="flex items-center gap-4">
                                    <span className="grid place-items-center w-11 h-11 rounded-md bg-obsidian text-cyan"><c.icon size={18} /></span>
                                    <div>
                                        <div className="eyebrow text-muted-foreground">{c.k}</div>
                                        <div className="font-medium text-obsidian">{c.v}</div>
                                    </div>
                                </div>
                            ))}
                        </Reveal>
                    </div>

                    <div className="lg:col-span-7">
                        <Reveal delay={0.1}>
                            <form onSubmit={submit} data-testid="inquiry-form" className="bg-card border border-border rounded-2xl p-8 sm:p-10 space-y-5">
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <div>
                                        <label className="eyebrow text-muted-foreground block mb-2">{t.contact.fName} *</label>
                                        <input data-testid="inquiry-name" className={inputCls} value={form.name} onChange={set("name")} placeholder={t.contact.phName} />
                                    </div>
                                    <div>
                                        <label className="eyebrow text-muted-foreground block mb-2">{t.contact.fEmail} *</label>
                                        <input data-testid="inquiry-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder={t.contact.phEmail} />
                                    </div>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <div>
                                        <label className="eyebrow text-muted-foreground block mb-2">{t.contact.fPhone}</label>
                                        <input data-testid="inquiry-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder={t.contact.phPhone} />
                                    </div>
                                    <div>
                                        <label className="eyebrow text-muted-foreground block mb-2">{t.contact.fCompany}</label>
                                        <input data-testid="inquiry-company" className={inputCls} value={form.company} onChange={set("company")} placeholder={t.contact.phCompany} />
                                    </div>
                                </div>
                                <div>
                                    <label className="eyebrow text-muted-foreground block mb-2">{t.contact.fInterested}</label>
                                    <select data-testid="inquiry-product" className={inputCls} value={form.product} onChange={set("product")}>
                                        <option value="">{t.contact.fSelect}</option>
                                        {products.map((p) => <option key={p.slug} value={p.name}>{p.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="eyebrow text-muted-foreground block mb-2">{t.contact.fMessage} *</label>
                                    <textarea data-testid="inquiry-message" rows={5} className={inputCls} value={form.message} onChange={set("message")} placeholder={t.contact.phMessage} />
                                </div>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="inquiry-submit-button"
                                    className="group eyebrow inline-flex items-center gap-2 rounded-full bg-obsidian text-white px-8 py-4 hover:-translate-y-0.5 hover:bg-cyan hover:text-obsidian transition-all duration-300 disabled:opacity-50"
                                >
                                    {submitting ? t.cta.sending : t.cta.sendInquiry} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                                </button>
                            </form>
                        </Reveal>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}
